import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { Account } from '@core/models';
import { plural } from 'pluralize';
import {
  Actions,
  NgxsModule,
  ofActionCompleted,
  ofActionDispatched,
  ofActionErrored,
  ofActionSuccessful,
  Store,
} from '@ngxs/store';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { RelatedToType } from '@shared/enums';
import { CustomObjectValue } from '@shared/modules/custom-objects/custom-objects.types';
import { CustomObjectState } from '@shared/modules/custom-objects/state/state';
import { FileResource, FolderResource } from '@shared/modules/folders/folders.types';
import { Folders } from '@shared/modules/folders/state/actions';
import { AccountState } from '@state/account/state';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { Task, TaskListTemplate, TaskRelatedToModule, TaskView } from '@tasks/tasks.types';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { Project, Property } from 'app/modules/projects/projects.types';
import { filter, switchMap } from 'rxjs';
import { TsButtonModule, TsSnackbarService } from 'tsui';

@Component({
  selector: 'app-task-details-files',
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyProgressSpinnerModule,
    MatIconModule,
    MatLegacyTooltipModule,
    MatDividerModule,
    NgxsModule,
    TsButtonModule,
    DirectivesModule,
  ],
  template: `
    <div class="flex flex-col mt-3">
      <div class="mb-3">
        <div class="font-medium">Files</div>
        <mat-divider
          role="separator"
          class="mat-divider mt-1 mb-1 mat-divider-horizontal"
          aria-orientation="horizontal"
        >
        </mat-divider>
        <input style="display: none" type="file" (change)="onFileChanged($event)" #fileInput />

        <ts-button
          class="xs:hidden add-task"
          mat-flat-ts-button
          (click)="fileInput.click()"
          type="flat"
          color="basic"
          [appAuthorize]="canEditTask"
          denyAction="disable"
        >
          <ng-container *ngIf="loadingAttachment; else attachmentIcon">
            <mat-spinner class="icon-size-20 mr-2" [mode]="'indeterminate'" [color]="'primary'"></mat-spinner>
          </ng-container>
          <ng-template #attachmentIcon>
            <mat-icon class="icon-size-20 mr-2" [svgIcon]="'backup'"></mat-icon>
          </ng-template>
          Upload Attachment
        </ts-button>
      </div>
      <div>
        <div class="flex" *ngFor="let fileResource of task?.file_resources">
          <div class="text-sm">
            <a
              [class.pointer]="fileResource.documents.length > 0"
              (click)="downloadDocument(fileResource)"
              matTooltip="Processing..."
              [matTooltipDisabled]="fileResource.documents.length > 0"
            >
              <mat-icon class="icon" [svgIcon]="'insert_drive_file'"></mat-icon>
            </a>
          </div>
          <div class="mx-2 w-100">
            <a
              class="break-all text-sm"
              (click)="downloadDocument(fileResource)"
              matTooltip="Processing..."
              [matTooltipDisabled]="fileResource.documents.length > 0"
            >
              {{ fileResource.name }}
            </a>
          </div>
          <div class="text-sm min-w-22">
            <p>{{ fileResource.created_at | date: 'MMM dd, y' }}</p>
            <p>{{ fileResource.user?.full_name }}</p>
          </div>
          <div class="ml-2">
            <button mat-icon-button class="delete-file" (click)="deleteFile(fileResource)">
              <mat-icon [svgIcon]="'delete'"></mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrl: './task-details-files.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsFilesComponent implements OnInit {
  private _task: Task;
  private _taskView: TaskView;
  relatedToType: string;

  actions$ = inject(Actions);
  destroyRef = inject(DestroyRef);
  @Input()
  set task(value: Task) {
    this._task = value;
    this.relatedToType = this._task.related_to_type;
  }

  get task(): Task {
    return this._task;
  }
  @Input()
  set taskView(value: TaskView) {
    this._taskView = value;
    this.task_list_template_id = +value?.task_list_template_id;
  }

  @Input() canEditTask: boolean;
  @Input() isGlobalTask: boolean;

  accountFoldersService = inject(AccountFoldersService);
  store = inject(Store);
  snackBar = inject(TsSnackbarService);
  confirmService = inject(ConfirmService);
  loadingAttachment: boolean;
  account = this.store.selectSnapshot(AccountState.getAccount);
  task_list_template_id;
  custom_object_id;

  ngOnInit(): void {
    this.handleUploadAttachmentFeedback();
  }

  downloadDocument(fileResource: FileResource): void {
    if (fileResource.documents.length > 0) {
      let document = fileResource.documents[0];
      window.open(document.original_url, '_blank');
    } else {
      // reload task to get the document
      this.loadTask();
    }
  }

  deleteFile(fileResource: FileResource): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result: boolean) => result),
        switchMap(() => {
          const module = this.getFoldersModule();
          return this.store.dispatch([
            new Folders.UpdateFolderModule(module.moduleUrl, module.moduleId),
            new Folders.DeleteFile(fileResource.folder_resource_id, fileResource.id, {
              task_list_template_id: this.task_list_template_id,
            }),
          ]);
        }),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Successfully deleted attachment', 'success');
          this.loadTask();
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open(error.error.file_resource.errors, 'error');
        },
      });
  }

  onFileChanged($event): void {
    let fileToUpload = $event.target.files[0];
    if (!fileToUpload) {
      return;
    }

    let fileResource: FileResource = {};
    fileResource.name = fileToUpload.name;
    fileResource.related_to_type = RelatedToType.Task;
    fileResource.related_to_id = this.task.id;

    const module = this.getFoldersModule();
    this.store.dispatch([
      new Folders.UpdateFolderModule(module.moduleUrl, module.moduleId),
      new Folders.CreateFile(module.rootFolderId, fileToUpload, {
        relatedToType: RelatedToType.Task,
        relatedToId: this.task.id,
        task_list_template_id: this.task_list_template_id,
        customObjectId: this.custom_object_id,
      }),
    ]);
  }

  loadTask(): void {
    if (this.task.id && this.task.id !== -1) {
      this.store.dispatch(new TaskActions.GetTaskDetail(this.task.task_list_id, this.task.id));
    }
  }

  private getFoldersModule(): { moduleUrl: string; moduleId: number; rootFolderId: number } {
    switch (this.relatedToType) {
      case RelatedToType.CustomObjectValue:
        return {
          moduleUrl: RelatedToType.CustomObjectValue,
          moduleId: +this.task.related_to_id,
          rootFolderId: +this.task.root_folder_resource_id,
        };
      case RelatedToType.Account:
        return {
          moduleUrl: RelatedToType.Account,
          moduleId: +this.task.related_to_id,
          rootFolderId: +this.task.root_folder_resource_id,
        };
      case RelatedToType.TaskListTemplate:
        return {
          moduleUrl: RelatedToType.TaskListTemplate,
          moduleId: +this.task.related_to_id,
          rootFolderId: +this.task.root_folder_resource_id,
        };
      default:
        return {
          moduleUrl: plural(this.task.related_to_type.toLocaleLowerCase()),
          moduleId: +this.task.related_to_id,
          rootFolderId: +this.task.root_folder_resource_id,
        };
    }
  }

  private handleUploadAttachmentFeedback(): void {
    // Display loading spinner icon after user selects an attachment to upload.
    this.actions$.pipe(takeUntilDestroyed(this.destroyRef), ofActionDispatched(Folders.CreateFile)).subscribe(() => {
      this.loadingAttachment = true;
    });

    // Hide loading spinner icon after action has completed and load the tasks.
    this.actions$.pipe(takeUntilDestroyed(this.destroyRef), ofActionCompleted(Folders.CreateFile)).subscribe(() => {
      this.loadingAttachment = false;
      this.loadTask();
    });

    // If upload is successful, display success message in snackBar.
    this.actions$.pipe(takeUntilDestroyed(this.destroyRef), ofActionSuccessful(Folders.CreateFile)).subscribe(() => {
      this.snackBar.open('Successfully uploaded attachment', 'success');
    });

    // If upload is not successful, display error message in snackBar.
    this.actions$.pipe(takeUntilDestroyed(this.destroyRef), ofActionErrored(Folders.CreateFile)).subscribe(() => {
      this.snackBar.open('Unable to upload an attachment at this moment. Please try again later.', 'error');
    });
  }
}
